import React, { useState } from 'react';
import { Button, Tooltip, message } from 'antd';
import { AimOutlined } from '@ant-design/icons';
import axios from 'axios';

const GeocodingButton = ({ 
  value, 
  type = 'city', 
  country = 'IT', 
  onGeocode,
  disabled = false 
}) => {
  const [loading, setLoading] = useState(false);


  // Central postal codes for major European cities to get better city center results
  const centralPostcodes = {
    // Italy - both Italian and English names
    'milano': '20121',
    'milan': '20121',
    'roma': '186', // Using 186 instead of 00186 since GISCO removes leading zeros
    'rome': '186',
    'firenze': '50122',
    'florence': '50122',
    'napoli': '80100',
    'naples': '80100',
    'torino': '10121',
    'turin': '10121',
    'bologna': '40100',
    'genova': '16121',
    'genoa': '16121',
    'palermo': '90100',
    'venezia': '30100',
    'venice': '30100',
    'verona': '37100',
    
    // France
    'paris': '75001',
    'marseille': '13001',
    'lyon': '69001',
    'toulouse': '31000',
    'nice': '6000', // 06000 without leading zero
    'nantes': '44000',
    'strasbourg': '67000',
    'montpellier': '34000',
    'bordeaux': '33000',
    'lille': '59000',
    
    // Germany  
    'berlin': '10115',
    'hamburg': '20095',
    'münchen': '80331',
    'munich': '80331',
    'köln': '50667',
    'cologne': '50667',
    'frankfurt': '60311',
    'stuttgart': '70173',
    'düsseldorf': '40213',
    'dortmund': '44135',
    'essen': '45127',
    
    // Spain
    'madrid': '28013',
    'barcelona': '8002', // 08002 without leading zero
    'valencia': '46003',
    'sevilla': '41004',
    'seville': '41004',
    'zaragoza': '50001',
    'málaga': '29015',
    'malaga': '29015',
    'bilbao': '48009',
    
    // Netherlands
    'amsterdam': '1012',
    'rotterdam': '3011',
    'den haag': '2511',
    'the hague': '2511',
    'utrecht': '3511',
    'eindhoven': '5611',
    
    // Belgium
    'brussels': '1000',
    'bruxelles': '1000',
    'antwerp': '2000',
    'antwerpen': '2000',
    'gent': '9000',
    'ghent': '9000',
    'bruges': '8000',
    'brugge': '8000',
    
    // Portugal
    'lisboa': '1200',
    'lisbon': '1200',
    'porto': '4000',
    'oporto': '4000',
    
    // Austria
    'wien': '1010',
    'vienna': '1010',
    'salzburg': '5020',
    'graz': '8010',
    'innsbruck': '6020'
  };

  const handleGeocode = async () => {
    if (!value || !value.trim()) {
      message.warning('Please enter a value to geocode');
      return;
    }

    setLoading(true);
    
    try {
      // Country should now always be a valid 2-letter code from the dropdown
      const countryCode = country && country.length === 2 ? country.toUpperCase() : 'IT';
      console.log(`[GEOCODING] Using country code: ${countryCode}`);
      
      let result;
      
      if (type === 'city') {
        result = await searchForCityCenter(value.trim(), countryCode);
      } else {
        result = await searchByPostcode(value.trim(), countryCode);
      }
      
      if (result && onGeocode) {
        onGeocode(result);
        
        if (type === 'city') {
          if (result.fullAddress && result.fullAddress.includes('municipality center')) {
            message.success(`Found municipality center for ${result.city}`);
          } else {
            message.success(`Found city center for ${result.city}`);
          }
        } else {
          message.success('Location found successfully!');
        }
      } else {
        message.warning('No results found for the given search');
      }
    } catch (error) {
      console.error('Geocoding error:', error);
      message.error('Failed to geocode location: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const searchForCityCenter = async (cityName, country) => {
    // 1. First priority: Check backend municipality centroid API
    try {
      const backendCentroid = await searchBackendMunicipalityCentroid(cityName, country);
      if (backendCentroid) {
        return backendCentroid;
      }
    } catch (error) {
      console.warn('Backend municipality centroid lookup failed:', error.message);
    }
    
    // 2. Second priority: Check central postcode lookup
    const cityKey = cityName.toLowerCase();
    const centralPostcode = centralPostcodes[cityKey];
    
    if (centralPostcode) {
      // For major cities with known central postcodes, search using central postcode and calculate average
      return await searchWithCentralPostcode(cityName, centralPostcode, country);
    } else {
      // 3. Fallback: For other cities, get multiple results and calculate average
      return await searchWithMultipleResults(cityName, country);
    }
  };

  const searchBackendMunicipalityCentroid = async (cityName, country) => {
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT || 'http://localhost:8080/api';
      const url = `${apiEndpoint}/geocoding/municipality-centroid`;
      const params = { name: cityName, country: country };
      
      console.log('[GEOCODING] Backend municipality request:', { url, params });
      
      const response = await axios.get(url, { params });
      
      console.log('[GEOCODING] Backend municipality response:', response.data);
      
      const data = response.data;
      if (data && data.longitude && data.latitude) {
        const result = {
          longitude: data.longitude,
          latitude: data.latitude,
          city: data.name,
          country: data.country,
          fullAddress: `${data.name} (${data.note || 'municipality center'})`
        };
        console.log('[GEOCODING] Backend municipality result:', result);
        return result;
      }
      
      console.log('[GEOCODING] Backend municipality: invalid data format');
      return null;
    } catch (error) {
      if (error.response && error.response.status === 404) {
        console.log('[GEOCODING] Backend municipality: not found (404)');
        return null;
      }
      console.error('[GEOCODING] Backend municipality error:', error);
      throw new Error(`Backend API error: ${error.response?.status || error.message}`);
    }
  };

  const searchWithCentralPostcode = async (cityName, postcode, country) => {
    const params = new URLSearchParams();
    params.append('city', cityName);
    params.append('postcode', postcode);
    params.append('country', country);
    
    const url = `https://gisco-services.ec.europa.eu/addressapi/search?${params.toString()}`;
    console.log('[GEOCODING] GISCO central postcode request:', { url, cityName, postcode, country });
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('[GEOCODING] GISCO central postcode response:', data);
    
    if (data && data.results && data.results.length > 0) {
      // Calculate average coordinates from multiple central results
      const coordinates = data.results.map(r => r.XY).filter(xy => xy && xy.length === 2);
      
      if (coordinates.length > 0) {
        const avgLng = coordinates.reduce((sum, xy) => sum + xy[0], 0) / coordinates.length;
        const avgLat = coordinates.reduce((sum, xy) => sum + xy[1], 0) / coordinates.length;
        
        return {
          longitude: avgLng,
          latitude: avgLat,
          city: cityName,
          postcode: postcode,
          country: country,
          fullAddress: `${cityName} (city center)`
        };
      }
    }
    
    // Fallback to simple city search
    return await searchWithMultipleResults(cityName, country);
  };

  const searchWithMultipleResults = async (cityName, country) => {
    const params = new URLSearchParams();
    params.append('city', cityName);
    params.append('country', country);
    
    const response = await fetch(`https://gisco-services.ec.europa.eu/addressapi/search?${params.toString()}`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (data && data.results && data.results.length > 0) {
      // Take first 10 results and calculate average to get more central location
      const limitedResults = data.results.slice(0, 10);
      const coordinates = limitedResults.map(r => r.XY).filter(xy => xy && xy.length === 2);
      
      if (coordinates.length > 0) {
        const avgLng = coordinates.reduce((sum, xy) => sum + xy[0], 0) / coordinates.length;
        const avgLat = coordinates.reduce((sum, xy) => sum + xy[1], 0) / coordinates.length;
        
        const firstResult = data.results[0];
        return {
          longitude: avgLng,
          latitude: avgLat,
          city: firstResult.L2 || cityName,
          postcode: firstResult.PC || '',
          province: firstResult.L1 || '',
          country: firstResult.L0 || country,
          fullAddress: `${firstResult.L2 || cityName} (estimated center)`
        };
      }
    }
    
    return null;
  };

  const searchByPostcode = async (postcode, country) => {
    // For European postal codes, remove leading zeros as GISCO doesn't handle them well
    let cleanPostcode = postcode;
    if (postcode.startsWith('0') && ['IT', 'FR', 'ES', 'PT'].includes(country)) {
      cleanPostcode = postcode.replace(/^0+/, '');
    }
    
    const params = new URLSearchParams();
    params.append('postcode', cleanPostcode);
    params.append('country', country);

    const response = await fetch(`https://gisco-services.ec.europa.eu/addressapi/search?${params.toString()}`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (data && data.results && data.results.length > 0) {
      const firstResult = data.results[0];
      const coordinates = firstResult.XY;
      
      if (coordinates && coordinates.length === 2) {
        return {
          longitude: coordinates[0],
          latitude: coordinates[1],
          fullAddress: `${firstResult.TF || ''} ${firstResult.LD || ''}, ${firstResult.L2 || ''}, ${firstResult.PC || ''}`.trim(),
          city: firstResult.L2 || '',
          postcode: firstResult.PC || '',
          province: firstResult.L1 || '',
          country: firstResult.L0 || '',
          road: firstResult.TF || '',
          houseNumber: firstResult.LD || '',
          raw: firstResult
        };
      }
    }
    
    return null;
  };

  return (
    <Tooltip title={`Find ${type === 'city' ? 'city' : 'postal code'} coordinates`}>
      <Button
        icon={<AimOutlined />}
        size="small"
        onClick={handleGeocode}
        loading={loading}
        disabled={disabled || !value}
        style={{ marginLeft: 8 }}
      />
    </Tooltip>
  );
};

export default GeocodingButton;